import React, { useState, useEffect } from "react";
import {
  Layout as AntLayout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Space,
  Typography,
} from "antd";
import {
  DashboardOutlined,
  FileTextOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

interface LayoutProps {
  children: React.ReactNode;
  user: any;
  signOut: (data?: any) => void;
}

const Layout: React.FC<LayoutProps> = ({ children, user, signOut }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdmin, isAssessor, isViewer, user: authUser } = useAuth();

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const menuItems = [
    {
      key: "/dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
    },
    ...(isAssessor || isViewer
      ? [
          {
            key: "/assessments",
            icon: <FileTextOutlined />,
            label: "Assessments",
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            key: "/users",
            icon: <UserOutlined />,
            label: "User Management",
          },
        ]
      : []),
    {
      key: "/settings",
      icon: <SettingOutlined />,
      label: "Settings",
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
  };

  const handleSignOut = () => {
    signOut();
  };

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === "profile") {
      navigate("/settings");
    } else if (key === "signout") {
      handleSignOut();
    }
  };

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => handleUserMenuClick({ key: "profile" }),
    },
    {
      key: "signout",
      icon: <LogoutOutlined />,
      label: "Sign Out",
      onClick: () => handleUserMenuClick({ key: "signout" }),
    },
  ];

  const getSelectedKeys = () => {
    const path = location.pathname;
    if (path.startsWith("/assessments")) {
      return ["/assessments"];
    }
    if (path.startsWith("/settings")) {
      return ["/settings"];
    }
    return [path];
  };

  const sidebarContent = (showText: boolean) => (
    <>
      <div
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: showText ? "flex-start" : "center",
          gap: 10,
          padding: showText ? "0 16px" : "0",
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 16,
        }}
      >
        <img
          src="/ControlDesign.png"
          alt="Control Design Pro"
          style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0 }}
        />
        {showText && (
          <Text
            strong
            style={{
              fontSize: 15,
              color: "#0f1729",
              letterSpacing: "-0.3px",
              lineHeight: "1.2",
            }}
          >
            Control Design
            <br />
            <span style={{ color: "#0ea5e9", fontWeight: 800 }}>Pro</span>
          </Text>
        )}
      </div>

      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        items={menuItems}
        onClick={handleMenuClick}
        style={{ border: "none" }}
      />
    </>
  );

  return (
    <AntLayout style={{ minHeight: "100vh" }}>
      {/* Mobile backdrop overlay */}
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 199,
            background: "rgba(0,0,0,0.45)",
          }}
        />
      )}

      {isMobile ? (
        /* Mobile: fixed slide-in sidebar */
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            height: "100vh",
            width: 220,
            background: "#fff",
            zIndex: 200,
            overflow: "auto",
            transform: mobileOpen ? "translateX(0)" : "translateX(-220px)",
            transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
            boxShadow: mobileOpen
              ? "4px 0 20px rgba(29,35,41,0.12)"
              : "none",
          }}
        >
          {sidebarContent(true)}
        </div>
      ) : (
        /* Desktop: sticky collapsible Sider */
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          style={{
            background: "#fff",
            boxShadow: "2px 0 8px 0 rgba(29,35,41,.05)",
            overflow: "auto",
            height: "100vh",
            position: "sticky",
            top: 0,
            left: 0,
          }}
        >
          {sidebarContent(!collapsed)}
        </Sider>
      )}

      <AntLayout>
        <Header
          style={{
            padding: "0 16px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 2px 8px 0 rgba(29,35,41,.05)",
            position: "sticky",
            top: 0,
            zIndex: 10,
            height: 64,
            lineHeight: "64px",
          }}
        >
          <Space>
            <Button
              type="text"
              icon={
                isMobile
                  ? mobileOpen
                    ? <MenuFoldOutlined />
                    : <MenuUnfoldOutlined />
                  : collapsed
                  ? <MenuUnfoldOutlined />
                  : <MenuFoldOutlined />
              }
              onClick={() =>
                isMobile
                  ? setMobileOpen(!mobileOpen)
                  : setCollapsed(!collapsed)
              }
              style={{ fontSize: "16px", width: 64, height: 64 }}
            />

            {isAssessor && location.pathname === "/assessments" && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate("/assessments/new")}
              >
                {isMobile ? "New" : "New Assessment"}
              </Button>
            )}
          </Space>

          <Space>
            {!isMobile && (
              <Text>
                {user?.firstName} {user?.lastName}
              </Text>
            )}
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <Avatar
                style={{ backgroundColor: "#1890ff", cursor: "pointer" }}
                icon={<UserOutlined />}
              />
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: isMobile ? "12px 8px" : "24px 16px",
            padding: isMobile ? 14 : 24,
            background: "#fff",
            minHeight: 280,
            borderRadius: 8,
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default Layout;
